import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/Layout";
import SignaturePad from "@/components/SignaturePad";
import MaterialsPicker from "@/components/MaterialsPicker";
import MaterialsUsedPicker from "@/components/MaterialsUsedPicker";
import ChargeCodesPicker from "@/components/ChargeCodesPicker";
import AdminEditJob from "@/components/AdminEditJob";
import { colourHex } from "@/lib/pascoColours";
import { formatDate, formatDateTime } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, MapPin, Phone, Navigation, Play, CheckCircle2, Camera,
  FileClock, User, Building2, Clock, CalendarDays, FileText, Share2, Droplet, RotateCcw, Receipt,
  PenLine, Send, Mail, Smartphone, Timer, History, Pencil, AlertTriangle, Link as LinkIcon, ArrowRight, Plus, Trash2, Layers,
  Ban, Undo2,
} from "lucide-react";
import { toast } from "sonner";

const TIMELINE_LABELS = {
  work_completed: "Work completed by technician",
  docket_sent: "Docket sent to customer for signature",
  docket_opened: "Customer opened the docket",
  docket_signed: "Docket signed by customer",
  signed_on_device: "Signed on device",
  reminder_sent: "Reminder sent",
  signing_expired: "Signing link expired",
  visit_return_tomorrow: "Visit completed — returning tomorrow",
  visit_awaiting_return_visit: "Day completed — awaiting return visit",
  visit_ready_for_invoice: "Job completed — ready for invoice",
  admin_edit: "Administrator edited the docket",
  work_notes_saved: "Work notes saved",
};

const COMPLETION_OPTIONS = [
  { key: "return_tomorrow", title: "Return Tomorrow", desc: "Work started, returning next day. Auto-schedules tomorrow." },
  { key: "awaiting_return_visit", title: "Day Completed – Awaiting Return Visit", desc: "Today's work done; pick the date you need to return." },
  { key: "ready_for_invoice", title: "Complete – Ready for Invoice", desc: "All work complete. Closes the job for invoicing." },
];

// Compute labour hours from "HH:MM" arrival/finish minus unpaid break (minutes). Returns rounded hours or null.
function computeHours(arrival, finish, breakMin) {
  if (!arrival || !finish) return null;
  const [ah, am] = arrival.split(":").map(Number);
  const [fh, fm] = finish.split(":").map(Number);
  if ([ah, am, fh, fm].some((n) => Number.isNaN(n))) return null;
  let mins = (fh * 60 + fm) - (ah * 60 + am) - (parseInt(breakMin, 10) || 0);
  if (mins <= 0) return null;
  return Math.round((mins / 60) * 100) / 100;
}

// Monday (ms) of the Mon–Sun week containing an ISO date; used to detect same billing week.
function weekMondayTs(iso) {
  const d = new Date(`${(iso || "").slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const day = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
function sameBillingWeek(a, b) {
  const ma = weekMondayTs(a), mb = weekMondayTs(b);
  return ma != null && mb != null && mb <= ma;
}


function PhotoSection({ job, type, reload, disabled }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const photos = job[type === "before" ? "before_photos" : "after_photos"] || [];
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("photo_type", type); fd.append("file", file);
    try {
      await api.post(`/jobs/${job.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${type === "before" ? "Before" : "After"} photo uploaded`); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400">{type} Photos</h4>
        {!disabled && (
          <button data-testid={`upload-${type}-photo-button`} onClick={() => inputRef.current.click()} disabled={uploading}
            className="text-sm font-medium text-blue-700 flex items-center gap-1"><Camera className="w-4 h-4" />{uploading ? "Uploading…" : "Add"}</button>
        )}
        <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={upload} className="hidden" />
      </div>
      {photos.length === 0 ? <p className="text-slate-300 text-sm">No {type} photos.</p> : (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p, i) => (
            <a key={i} href={fileUrl(p.path)} target="_blank" rel="noreferrer">
              <img src={fileUrl(p.path)} alt={`${type} ${i}`} data-testid={`${type}-photo-${i}`} className="w-full h-24 object-cover rounded-lg border border-slate-200" />
            </a>
          ))}
        </div>
      )}
      {!disabled && <p className="text-[11px] text-slate-400 mt-1.5">Photos are automatically time-stamped with the job number.</p>}
    </div>
  );
}

function CaptureSignature({ value, onChange, testid }) {
  if (value) {
    return (
      <div className="border border-slate-200 rounded-xl p-3">
        <img src={value} alt="signature" data-testid={`${testid}-preview`} className="h-20 object-contain" />
        <button type="button" data-testid={`${testid}-redo`} onClick={() => onChange("")} className="mt-2 text-xs text-blue-700 flex items-center gap-1"><RotateCcw className="w-3 h-3" />Redo signature</button>
      </div>
    );
  }
  return <div data-testid={testid}><SignaturePad onSave={onChange} saving={false} /></div>;
}

const money = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function AdminNoteEditor({ jobId, index, initial, reload }) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!note.trim()) { toast.error("Please enter the daily notes."); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/day-notes`, { mode: "admin", docket_index: index, daily_note: note });
      toast.success("Daily notes saved."); setEditing(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!editing) return (
    <button data-testid={`admin-enter-notes-${index}`} onClick={() => setEditing(true)}
      className="text-xs font-semibold text-navy border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">Enter notes (office)</button>
  );
  return (
    <div className="w-full mt-1">
      <Textarea data-testid={`admin-notes-input-${index}`} rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Enter the works performed / daily notes for this day…" />
      <div className="flex gap-2 mt-2">
        <Button data-testid={`admin-notes-save-${index}`} size="sm" onClick={save} disabled={busy} className="bg-navy hover:bg-navy-light">{busy ? "Saving…" : "Save notes"}</Button>
        <Button size="sm" variant="outline" onClick={() => { setEditing(false); setNote(initial || ""); }}>Cancel</Button>
      </div>
    </div>
  );
}

const DAY_STATUS = {
  completed: { label: "Completed", cls: "text-green-700 bg-green-50 border-green-200", Icon: CheckCircle2 },
  awaiting_signature: { label: "Awaiting Signature", cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: PenLine },
  in_progress: { label: "In Progress", cls: "text-blue-700 bg-blue-50 border-blue-200", Icon: Clock },
  scheduled: { label: "Scheduled", cls: "text-slate-600 bg-slate-50 border-slate-200", Icon: CalendarDays },
};
const DayField = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-100 rounded-lg p-2 text-center">
    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
    <p className="text-sm font-semibold text-navy">{value}</p>
  </div>
);
const DayBlock = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">{label}</p>
    <p className="text-sm text-slate-700 whitespace-pre-wrap">{value}</p>
  </div>
);

// A worker's saved running notes for ONE day (per worker + per day; older drafts only if same day).
const draftForWorker = (j, wid, day) => {
  const d = (day || "").slice(0, 10);
  const m = j.work_notes_drafts || {};
  if (m[`${wid}__${d}`]) return m[`${wid}__${d}`];
  return [m[wid], j.work_notes_draft].find((c) => c && (c.date || "").slice(0, 10) === d && (!c.worker_id || c.worker_id === wid)) || null;
};

function AdminDayManager({ job, reload, onCompleteDay }) {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [delDay, setDelDay] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [notesDay, setNotesDay] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [reassignDay, setReassignDay] = useState(null);
  const [reassignIds, setReassignIds] = useState([]);
  const [dayWorkerId, setDayWorkerId] = useState("");
  const [savingWorker, setSavingWorker] = useState(false);
  const saveDayWorker = async (index, wid) => {
    if (index == null || !wid) return;
    setSavingWorker(true);
    try {
      await api.post(`/jobs/${job.id}/dockets/${index}/reassign-worker`, { worker_id: wid });
      toast.success("Day worker updated — cost & timesheet re-flowed.");
      setDayDetail(null); setDayWorkerId(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update worker"); }
    finally { setSavingWorker(false); }
  };
  const [busy, setBusy] = useState(false);
  const [voidDay, setVoidDay] = useState(null);
  const submitVoid = async () => {
    const { index, voided } = voidDay;
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/dockets/${index}/${voided ? "unvoid" : "void"}`);
      toast.success(voided ? "Day reinstated to billing, dockets & timesheet." : "Day voided — removed from billing, customer documents & timesheet.");
      if (r.data?.timesheet_locked) toast.warning("The worker's timesheet for that week is already approved — adjust it manually.");
      setVoidDay(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const [form, setForm] = useState({ date: "", start_time: "08:00", worker_ids: [], notes: "" });
  useEffect(() => { api.get("/assignable-workers").then((r) => setWorkers(r.data)).catch(() => {}); }, []);
  const completed = job.status === "completed";
  // Every scheduled day gets its own row so admin can complete each one individually.
  let allDays = (job.schedule_days || []).map((d, i) => ({ ...d, date: (d.date || "").slice(0, 10), _ri: i }));
  if (!allDays.length && job.appointment_date) {
    allDays = [{ date: (job.appointment_date || "").slice(0, 10), start_time: job.start_time, _ri: 0,
      worker_name: job.technician_name,
      worker_ids: (job.technician_ids && job.technician_ids.length) ? job.technician_ids : (job.technician_id ? [job.technician_id] : []) }];
  }
  allDays = allDays.filter((d) => d.date).sort((a, b) => a.date.localeCompare(b.date));
  // One row per worker per day — each person gets their own docket / time card.
  const nameById = Object.fromEntries(workers.map((w) => [w.id, w.name]));
  allDays = allDays.flatMap((d) => {
    const ids = d.worker_ids || [];
    if (ids.length < 2) return [d];
    const names = (d.worker_name || "").split(", ");
    return ids.map((wid, k) => ({ ...d, worker_ids: [wid], _allIds: ids, _ri: `${d._ri}-${wid}`,
      worker_name: nameById[wid] || (names.length === ids.length ? names[k] : d.worker_name) }));
  });
  const draftOf = (d) => draftForWorker(job, (d.worker_ids || [])[0], d.date);

  // Per-worker matching: assign each non-archived docket to AT MOST ONE row. Two workers rostered on
  // the same date each get their own docket/day — completing one no longer closes the other.
  const activeDockets = [];
  (job.dockets || []).forEach((d, idx) => { if (!d.archived && !(d.office_entry && d.notes_outstanding)) activeDockets.push({ ...d, _index: idx }); });
  const dwid = (dk) => dk.worker_id || dk.completed_by_id;
  const rowDocket = {}; const usedDk = new Set();
  allDays.forEach((d) => {
    const wids = d.worker_ids || [];
    const m = activeDockets.find((dk) => !usedDk.has(dk._index) && (dk.visit_date || "").slice(0, 10) === d.date && dwid(dk) && wids.includes(dwid(dk)));
    if (m) { rowDocket[d._ri] = m; usedDk.add(m._index); }
  });
  allDays.forEach((d) => {           // legacy dockets with no worker_id → first unmatched row on that date
    if (rowDocket[d._ri]) return;
    const m = activeDockets.find((dk) => !usedDk.has(dk._index) && (dk.visit_date || "").slice(0, 10) === d.date && !dwid(dk));
    if (m) { rowDocket[d._ri] = m; usedDk.add(m._index); }
  });
  // A docket stamped to someone not rostered on that date still gets its own row (never borrows another person's).
  activeDockets.filter((dk) => !usedDk.has(dk._index)).forEach((dk) => {
    const row = { date: (dk.visit_date || "").slice(0, 10), start_time: dk.start_time, worker_ids: dwid(dk) ? [dwid(dk)] : [],
      worker_name: dk.technician_name || dk.completed_by, _ri: `dk-${dk._index}`, _extra: true };
    allDays.push(row); rowDocket[row._ri] = dk; usedDk.add(dk._index);
  });
  allDays.sort((a, b) => a.date.localeCompare(b.date));

  // Pending (awaiting-signature) snapshots — matched per worker the same way.
  const activePending = (job.pending_dockets || []).map((p, idx) => ({ ...p, _pidx: idx }));
  const rowPending = {}; const usedPend = new Set();
  allDays.forEach((d) => {
    const wids = d.worker_ids || [];
    const m = activePending.find((p) => !usedPend.has(p._pidx) && (p.visit_date || "").slice(0, 10) === d.date && (p.worker_id || p.completed_by_id) && wids.includes(p.worker_id || p.completed_by_id));
    if (m) { rowPending[d._ri] = m; usedPend.add(m._pidx); }
  });
  allDays.forEach((d) => {
    if (rowDocket[d._ri] || rowPending[d._ri]) return;
    const m = activePending.find((p) => !usedPend.has(p._pidx) && (p.visit_date || "").slice(0, 10) === d.date && !(p.worker_id || p.completed_by_id));
    if (m) { rowPending[d._ri] = m; usedPend.add(m._pidx); }
  });

  const awaitingDate = (job.awaiting_signature_date || (job.status === "awaiting_signature" ? (job.appointment_date || "").slice(0, 10) : "") || "").slice(0, 10);
  const dayStatus = (d) => {
    if (rowDocket[d._ri]) return "completed";
    if (rowPending[d._ri]) return "awaiting_signature";
    if (!(job.pending_dockets || []).length && job.status === "awaiting_signature" && d.date === awaitingDate) return "awaiting_signature";
    if (job.status === "in_progress" && d.date === (job.appointment_date || "").slice(0, 10)) return "in_progress";
    return "scheduled";
  };
  const dayData = (d) => {
    const status = dayStatus(d);
    const dk = rowDocket[d._ri];
    if (dk) return { status, source: "docket", info: dk, index: dk._index };
    const snap = rowPending[d._ri];
    if (snap) {
      return { status, source: "pending", info: { ...snap, signing: snap.signing } };
    }
    if (status === "awaiting_signature") {
      return { status, source: "job", info: {
        time_on_site: job.time_on_site, description_of_works: job.description_of_works,
        customer_comments: job.customer_comments, materials_used: job.materials_used,
        charge_codes: job.charge_codes, sealant_product: job.sealant_product, sealant_colour: job.sealant_colour,
        before_photos: job.before_photos, after_photos: job.after_photos, signing: job.signing,
      } };
    }
    return { status, source: "none", info: {} };
  };
  const todayIso = new Date().toISOString().slice(0, 10);
  const submitDayNotes = async (mode) => {
    if (mode === "admin" && !noteText.trim()) { toast.error("Please enter the daily notes."); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/day-notes`, { mode, visit_date: notesDay.date, daily_note: noteText, worker_id: (notesDay.worker_ids || [])[0] || null });
      toast.success(mode === "admin" ? "Daily notes recorded." : "Sent to the worker to complete.");
      setNotesDay(null); setNoteText(""); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const downloadDocket = async (i, dn) => {
    try {
      const res = await api.get(`/jobs/${job.id}/dockets/${i}/docket`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${dn || "docket"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate docket"); }
  };

  const reopen = async () => {
    setBusy(true);
    try { await api.post(`/jobs/${job.id}/reopen-job`); toast.success("Job reopened — you can now add another day."); setReopenOpen(false); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addDay = async () => {
    if (!form.date) { toast.error("Please choose a date."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/add-day`, form);
      setAddOpen(false); setForm({ date: "", start_time: "08:00", worker_ids: [], notes: "" });
      if (r.data?.split) {
        toast.success(r.data.message || "A new job was created for this billing period.");
        if (r.data.new_job_id) navigate(`/jobs/${r.data.new_job_id}`);
        else reload();
      } else {
        toast.success("Day added to the job & calendar."); reload();
      }
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeDay = async () => {
    setBusy(true);
    try {
      if (delDay._allIds) {
        const rest = delDay._allIds.filter((x) => x !== delDay.worker_ids[0]);
        await api.post(`/jobs/${job.id}/reassign-day`, { date: delDay.date, worker_ids: rest });
        toast.success(`${delDay.worker_name || "Worker"} removed from this day.`);
      } else {
        await api.post(`/jobs/${job.id}/remove-day`, { kind: "scheduled", date: delDay.date });
        toast.success("Day removed from the job & calendar.");
      }
      setDelDay(null); reload();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleWorker = (id) => setForm((f) => ({ ...f, worker_ids: f.worker_ids.includes(id) ? f.worker_ids.filter((x) => x !== id) : [...f.worker_ids, id] }));
  const openReassign = (d) => { setReassignDay(d); setReassignIds(d.worker_ids || []); };
  const toggleReassign = (id) => setReassignIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const saveReassign = async () => {
    if (!reassignIds.length) { toast.error("Choose at least one team member."); return; }
    setBusy(true);
    try {
      const ids = reassignDay._allIds ? [...new Set([...reassignDay._allIds.filter((x) => x !== reassignDay.worker_ids[0]), ...reassignIds])] : reassignIds;
      await api.post(`/jobs/${job.id}/reassign-day`, { date: reassignDay.date, worker_ids: ids }); toast.success("Day reassigned — calendar updated."); setReassignDay(null); reload();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-4" data-testid="admin-day-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-heading font-bold text-navy text-lg flex items-center gap-2"><CalendarDays className="w-5 h-5" />Job Days</h3>
        {completed ? (
          <Button size="sm" onClick={() => setReopenOpen(true)} data-testid="reopen-job-btn" className="bg-amber-600 hover:bg-amber-700"><RotateCcw className="w-4 h-4 mr-1.5" />Reopen Job</Button>
        ) : (
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-day-btn" className="bg-navy hover:bg-navy-light"><Plus className="w-4 h-4 mr-1.5" />Add Another Day</Button>
        )}
      </div>
      <div data-testid="job-billing-period" className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`px-2 py-0.5 rounded-full font-semibold ${job.billing_cycle === "monthly" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>
          Billing: {job.billing_cycle === "monthly" ? "Monthly" : "Weekly"}
        </span>
        {job.billing_period_label && (
          <span className="text-slate-500">Current period: <span className="font-semibold text-navy">{job.billing_period_label}</span></span>
        )}
        <span className="text-slate-400">· New days outside this period start a new job automatically.</span>
      </div>
      {completed && <p className="text-xs text-slate-400">This job is completed. Reopen it to book an additional workday — all existing dockets, records and invoicing stay intact.</p>}

      {allDays.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Scheduled days</p>
          {allDays.map((d, i) => {
            const isDone = !!rowDocket[d._ri];
            const isVoided = isDone && !!rowDocket[d._ri].voided;
            const st = dayStatus(d);
            const M = DAY_STATUS[st];
            const rowWid = (d.worker_ids || [])[0] || null;
            const rk = (d._allIds || d._extra) ? `${d.date}-${rowWid}` : d.date;
            const dr = draftOf(d);
            return (
              <div key={i} data-testid={`sched-day-${rk}`} className={`flex items-center justify-between gap-2 flex-wrap rounded-xl border px-3 py-2 ${isVoided ? "border-red-200 bg-red-50/40" : "border-slate-100"}`}>
                <button type="button" data-testid={`view-day-${rk}`} onClick={() => { setDayDetail(d); setDayWorkerId(""); }} className={`min-w-0 text-left flex-1 group ${isDone ? "min-w-full sm:min-w-0" : ""}`}>
                  <p className={`font-semibold text-sm group-hover:underline ${isVoided ? "text-slate-400 line-through" : "text-navy"}`}>{formatDate(d.date)} · {d.start_time || "—"}</p>
                  <p className="text-xs text-slate-500 truncate">{d.worker_name || "Unassigned"}{d.notes ? ` · ${d.notes}` : ""} · <span className="text-navy/70">View details</span></p>
                  {!isDone && dr && (dr.date || "").slice(0, 10) === d.date && dr.text && (
                    <span data-testid={`day-notes-updated-${rk}`} className="inline-flex items-center gap-1 mt-1 text-[11px] font-medium text-blue-600 bg-blue-50 rounded-full px-2 py-0.5">
                      <PenLine className="w-3 h-3" />Notes updated {formatDateTime(dr.updated_at)}
                    </span>
                  )}
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {isVoided ? (
                    <span data-testid={`day-status-${rk}`} title={`Voided by ${rowDocket[d._ri].voided_by || "admin"}`} className="inline-flex items-center gap-1 text-xs font-bold border rounded-full px-2.5 py-1 bg-red-100 text-red-700 border-red-200">
                      <Ban className="w-3.5 h-3.5" />VOIDED
                    </span>
                  ) : (
                    <span data-testid={`day-status-${rk}`} className={`inline-flex items-center gap-1 text-xs font-semibold border rounded-full px-2.5 py-1 ${M.cls}`}>
                      <M.Icon className="w-3.5 h-3.5" />{M.label}
                    </span>
                  )}
                  {isDone && (
                    <Button size="sm" variant="ghost" data-testid={`${isVoided ? "unvoid" : "void"}-day-${rk}`}
                      onClick={() => setVoidDay({ index: rowDocket[d._ri]._index, voided: isVoided, date: d.date, worker: d.worker_name })}
                      className={`text-xs h-8 ${isVoided ? "text-green-700 hover:bg-green-50" : "text-red-600 hover:bg-red-50"}`}>
                      {isVoided ? <><Undo2 className="w-3.5 h-3.5 mr-1" />Unvoid Day</> : <><Ban className="w-3.5 h-3.5 mr-1" />Void Day</>}
                    </Button>
                  )}
                  {!isDone && (
                    <>
                      <Button size="sm" data-testid={`complete-day-${rk}`} onClick={() => onCompleteDay && onCompleteDay(d.date, rowWid)} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Complete Day as Admin
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`reassign-day-${rk}`} onClick={() => openReassign(d)} className="text-navy hover:bg-slate-100 text-xs h-8">Reassign</Button>
                      <Button size="sm" variant="ghost" data-testid={`day-notes-${rk}`} onClick={() => { setNotesDay(d); setNoteText(""); }} className="text-amber-700 hover:bg-amber-50 text-xs h-8"><FileText className="w-3.5 h-3.5 mr-1" />Notes</Button>
                      <Button size="sm" variant="ghost" data-testid={`del-day-${rk}`} onClick={() => setDelDay(d)} className="text-red-600 hover:bg-red-50 h-8"><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!voidDay} onOpenChange={(o) => !o && setVoidDay(null)}>
        <DialogContent className="max-w-md" data-testid="void-day-dialog">
          <DialogHeader><DialogTitle className="text-navy">{voidDay?.voided ? "Unvoid this day?" : "Void this day?"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {voidDay && `${formatDate(voidDay.date)} · ${voidDay.worker || "Unassigned"}. `}
            {voidDay?.voided
              ? "The day goes back into billing, invoice totals, customer dockets, cashflow, profitability and the worker's timesheet."
              : "The day stays on record as VOIDED but is removed from billing, invoice preview/final invoice, customer & combined dockets, cashflow, profitability and the worker's timesheet."}
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setVoidDay(null)} data-testid="void-day-cancel-btn">Cancel</Button>
            <Button onClick={submitVoid} disabled={busy} data-testid="void-day-confirm-btn" className={voidDay?.voided ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}>
              {voidDay?.voided ? "Unvoid Day" : "Void Day"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="max-w-md" data-testid="reopen-confirm-dialog">
          <DialogHeader><DialogTitle className="text-navy">Reopen this completed job?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This will allow additional workdays to be booked. Existing job information and completed work records will not be changed.</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setReopenOpen(false)}>Cancel</Button>
            <Button onClick={reopen} disabled={busy} data-testid="reopen-confirm-btn" className="bg-amber-600 hover:bg-amber-700">Reopen Job</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dayDetail} onOpenChange={(o) => !o && setDayDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="day-detail-dialog">
          {dayDetail && (() => {
            const dd = dayData(dayDetail);
            const M = DAY_STATUS[dd.status];
            const info = dd.info || {};
            const tos = info.time_on_site || {};
            const photos = [...(info.before_photos || []), ...(info.after_photos || [])];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-navy flex items-center gap-2"><CalendarDays className="w-5 h-5" />{formatDate(dayDetail.date)} · {dayDetail.start_time || "—"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Worker:</span><span className="font-medium text-navy">{dayDetail.worker_name || "Unassigned"}</span>
                    <span className={`ml-auto inline-flex items-center gap-1 text-xs font-semibold border rounded-full px-2.5 py-1 ${M.cls}`}><M.Icon className="w-3.5 h-3.5" />{M.label}</span>
                  </div>
                  {dd.source === "none" ? (
                    (draftOf(dayDetail) && (draftOf(dayDetail).date || "").slice(0, 10) === (dayDetail.date || "").slice(0, 10) && draftOf(dayDetail).text) ? (
                      <div data-testid="day-work-notes-draft" className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-blue-500 font-semibold mb-1 flex items-center gap-1">
                          <PenLine className="w-3.5 h-3.5" />Work notes (in progress)
                        </p>
                        <p className="text-slate-700 whitespace-pre-line">{draftOf(dayDetail).text}</p>
                        <p className="text-xs text-blue-500 mt-2">Last updated {formatDateTime(draftOf(dayDetail).updated_at)}{draftOf(dayDetail).updated_by ? ` · ${draftOf(dayDetail).updated_by}` : ""} — the worker is still on this day.</p>
                      </div>
                    ) : (
                      <p className="text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-3">No on-site details have been captured for this day yet. Times, materials, notes, photos and the signature will appear here once the day is completed on-site (or by admin).</p>
                    )
                  ) : (
                    <div className="space-y-3">
                      {dd.status === "awaiting_signature" && (
                        <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs flex items-center gap-1.5">
                          <PenLine className="w-3.5 h-3.5 shrink-0" />Docket sent for customer signature{info.signing?.customer_email ? ` to ${info.signing.customer_email}` : ""}{info.signing?.opened_at ? " · opened by customer" : ""}.
                        </p>
                      )}
                      {(tos.arrival_time || tos.finish_time || tos.hours_on_site != null) && (
                        <div className="grid grid-cols-3 gap-2">
                          <DayField label="On site" value={tos.arrival_time || "—"} />
                          <DayField label="Off site" value={tos.finish_time || "—"} />
                          <DayField label="Hours" value={tos.hours_on_site != null ? String(tos.hours_on_site) : "—"} />
                        </div>
                      )}
                      {info.description_of_works && <DayBlock label="Works performed" value={info.description_of_works} />}
                      {(info.sealant_product || info.sealant_colour) && <DayBlock label="Sealant" value={`${info.sealant_product || ""}${info.sealant_colour ? ` · ${info.sealant_colour}` : ""}`} />}
                      {(info.materials_used || []).length > 0 && <DayBlock label="Materials" value={(info.materials_used).map((m) => `${m.product || ""}${(m.colours || []).length ? " (" + m.colours.map((c) => c.name).join(", ") + ")" : ""}`).join("; ")} />}
                      {(info.charge_codes || []).length > 0 && <DayBlock label="Charge items" value={(info.charge_codes).map((c) => `${c.description || c.key} ×${c.qty}`).join("; ")} />}
                      {info.customer_comments && <DayBlock label="Customer comments" value={info.customer_comments} />}
                      {info.completed_by && <p className="text-xs text-slate-400">Completed by {info.completed_by}{info.completed_at ? ` · ${formatDateTime(info.completed_at)}` : ""}</p>}
                      {photos.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Photos ({photos.length})</p>
                          <div className="flex gap-2 flex-wrap">
                            {photos.slice(0, 8).map((p, idx) => (
                              <img key={idx} src={fileUrl(typeof p === "string" ? p : p.path)} alt="" className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
                            ))}
                          </div>
                        </div>
                      )}
                      {dd.source === "docket" && (
                        <Button size="sm" variant="outline" data-testid="day-detail-docket-pdf" onClick={() => downloadDocket(dd.index, info.docket_number)} className="w-full"><FileText className="w-4 h-4 mr-1.5" />Download Day Docket PDF</Button>
                      )}
                      {dd.source === "docket" && workers.length > 0 && (
                        <div className="pt-2 mt-1 border-t border-slate-100" data-testid="day-worker-editor">
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Worker for this day</p>
                          <p className="text-[11px] text-slate-500 mb-1.5">This is the single source of truth — it sets this day's labour cost (at the worker's rate), their timesheet hours and the docket PDF name.</p>
                          <div className="flex flex-wrap gap-2">
                            {workers.map((w) => {
                              const cur = dayWorkerId || info.worker_id || info.completed_by_id;
                              const sel = cur === w.id;
                              return (
                                <button key={w.id} type="button" data-testid={`day-worker-${w.id}`} onClick={() => setDayWorkerId(w.id)}
                                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${sel ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}>{w.name}</button>
                              );
                            })}
                          </div>
                          <Button size="sm" data-testid="save-day-worker" disabled={savingWorker || !dayWorkerId || dayWorkerId === (info.worker_id || info.completed_by_id)}
                            onClick={() => saveDayWorker(dd.index, dayWorkerId)} className="mt-2 bg-navy hover:bg-navy-light text-white text-xs h-8">
                            {savingWorker ? "Saving…" : "Save worker for this day"}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="add-day-dialog">
          <DialogHeader><DialogTitle className="text-navy">Add Another Day</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Date</Label><Input type="date" data-testid="add-day-date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div><Label className="text-xs">Start time</Label><Input type="time" data-testid="add-day-time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} /></div>
            <div>
              <Label className="text-xs">Assign to</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {workers.map((w) => (
                  <button key={w.id} type="button" data-testid={`add-day-worker-${w.id}`} onClick={() => toggleWorker(w.id)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${form.worker_ids.includes(w.id) ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}>{w.name}</button>
                ))}
              </div>
            </div>
            <div><Label className="text-xs">Notes (optional)</Label><Textarea data-testid="add-day-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addDay} disabled={busy} data-testid="add-day-confirm" className="bg-navy hover:bg-navy-light">Add Day</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassignDay} onOpenChange={(o) => !o && setReassignDay(null)}>
        <DialogContent className="max-w-md" data-testid="reassign-day-dialog">
          <DialogHeader><DialogTitle className="text-navy">Reassign {reassignDay ? formatDate(reassignDay.date) : "day"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Choose who should work this day. The job card and calendar update immediately.</p>
          <div className="flex flex-wrap gap-2 mt-1">
            {workers.map((w) => (
              <button key={w.id} type="button" data-testid={`reassign-worker-${w.id}`} onClick={() => toggleReassign(w.id)}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${reassignIds.includes(w.id) ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}>{w.name}</button>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setReassignDay(null)}>Cancel</Button>
            <Button onClick={saveReassign} disabled={busy} data-testid="reassign-confirm" className="bg-navy hover:bg-navy-light">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delDay} onOpenChange={(o) => !o && setDelDay(null)}>
        <DialogContent className="max-w-md" data-testid="del-day-dialog">
          <DialogHeader><DialogTitle className="text-navy">{delDay?._allIds ? `Remove ${delDay.worker_name || "this worker"} from this day?` : "Delete this scheduled day?"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{delDay?._allIds ? "Only this person is taken off the day. The other team members stay booked." : "This day will also be removed from the calendar and free up the assigned team member. The rest of the job will remain unchanged."}</p>
          {delDay?.notes && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-1 flex items-start gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />This day has notes recorded: “{delDay.notes}”. Removing it will discard them.</p>}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setDelDay(null)}>Cancel</Button>
            <Button onClick={removeDay} disabled={busy} data-testid="del-day-confirm" className="bg-red-600 hover:bg-red-700">Delete Day</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!notesDay} onOpenChange={(o) => !o && setNotesDay(null)}>
        <DialogContent className="max-w-md" data-testid="day-notes-dialog">
          {notesDay && (
            <>
              <DialogHeader><DialogTitle className="text-navy flex items-center gap-2"><FileText className="w-5 h-5" />Daily notes · {formatDate(notesDay.date)}</DialogTitle></DialogHeader>
              <p className="text-xs text-slate-600">Send this day back to <b>{notesDay.worker_name || "the worker"}</b> to fully complete on-site (times, photos, materials, notes &amp; signature) — their pay is held until done — or record the office daily notes on their behalf.</p>
              <Button size="sm" variant="outline" data-testid="day-notes-send" onClick={() => submitDayNotes("reopen")} disabled={busy} className="border-amber-400 text-amber-700 hover:bg-amber-100 mt-1"><Send className="w-4 h-4 mr-1.5" />Send to worker to complete</Button>
              <Textarea data-testid="day-notes-input" rows={4} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Or record the works performed / daily notes here…" className="mt-2" />
              {notesDay.date > todayIso && <p className="text-xs text-amber-600 mt-1">Office notes can only be saved for today or a past date.</p>}
              <div className="flex justify-end gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => setNotesDay(null)}>Cancel</Button>
                <Button size="sm" data-testid="day-notes-save" onClick={() => submitDayNotes("admin")} disabled={busy || notesDay.date > todayIso} className="bg-navy hover:bg-navy-light">{busy ? "Saving…" : "Save daily notes"}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function OfficeNotesCard({ job, reload }) {
  const today = new Date().toISOString().slice(0, 10);
  const [note, setNote] = useState("");
  const [date, setDate] = useState(job.appointment_date || today);
  const [busy, setBusy] = useState(false);
  const call = async (mode) => {
    if (mode === "admin" && !note.trim()) { toast.error("Please enter the daily notes."); return; }
    if (date > today) { toast.error("You can only record notes for today or a past date."); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/day-notes`, { mode, visit_date: date, daily_note: note });
      toast.success(mode === "admin" ? "Daily notes recorded." : "Sent to the worker to complete.");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="office-notes-card" className="border border-amber-200 bg-amber-50/40 rounded-xl p-4">
      {job.completion_outstanding ? (
        <p className="text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded-lg px-3 py-2 mb-3 flex items-start gap-1.5">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />Re-opened for {job.completion_outstanding.date} — awaiting the worker to fully complete this job on-site. Their pay is held until done.
        </p>
      ) : null}
      <h4 className="text-xs uppercase tracking-widest font-semibold text-amber-700 mb-1">Missing on-site notes</h4>
      <p className="text-xs text-slate-600 mb-3">No day was captured for this job (e.g. reception issues). Send it back to the assigned worker to fully complete on-site (times, photos, materials, notes &amp; signature) — their pay is held until done — or record the daily notes here on their behalf.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Day / date</Label>
          <Input data-testid="office-notes-date" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-44" />
        </div>
        <Button data-testid="office-send-worker" size="sm" variant="outline" onClick={() => call("reopen")} disabled={busy}
          className="border-amber-400 text-amber-700 hover:bg-amber-100">{job.completion_outstanding ? "Re-send to worker" : "Send to worker to complete"}</Button>
      </div>
      <Textarea data-testid="office-notes-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Or record the works performed / daily notes here…" className="mt-3" />
      <Button data-testid="office-notes-save" size="sm" onClick={() => call("admin")} disabled={busy} className="mt-2 bg-navy hover:bg-navy-light">{busy ? "Saving…" : "Save daily notes (office entry)"}</Button>
    </div>
  );
}


export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docketBusy, setDocketBusy] = useState(false);

  const [authMethod, setAuthMethod] = useState("sign_on_device");
  const [completionStatus, setCompletionStatus] = useState("ready_for_invoice");
  const [adminComplete, setAdminComplete] = useState(false);
  const [movingBilling, setMovingBilling] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [materialsUsed, setMaterialsUsed] = useState([]);
  const [chargeCodes, setChargeCodes] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [comments, setComments] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerSig, setCustomerSig] = useState("");
  const [techSig, setTechSig] = useState("");
  const [chargeLines, setChargeLines] = useState(null);
  const [arrivalTime, setArrivalTime] = useState("");
  const [finishTime, setFinishTime] = useState("");
  const [breakMinutes, setBreakMinutes] = useState("");
  const [returnVisitDate, setReturnVisitDate] = useState("");
  const [siteContact, setSiteContact] = useState({ name: "", position: "", phone: "", email: "" });
  const [swmsDialog, setSwmsDialog] = useState(false);
  const [swmsDoc, setSwmsDoc] = useState(null);
  const [swmsAgree, setSwmsAgree] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSavedAt, setNotesSavedAt] = useState(null);

  const load = () => api.get(`/jobs/${id}`).then(async (r) => {
    const j = r.data;
    setJob(j);
    if (user.role === "admin") {
      api.get(`/jobs/${id}/charge-lines`).then((cr) => setChargeLines(cr.data)).catch(() => setChargeLines(null));
    }
    let mats = j.materials_used || [];
    // Smart default: pre-fill from technician's last docket of the day (only for a fresh, not-yet-completed job)
    if (mats.length === 0 && j.status !== "completed" && j.status !== "awaiting_signature") {
      try {
        const last = await api.get(`/materials/last`);
        if (Array.isArray(last.data?.materials_used) && last.data.materials_used.length) mats = last.data.materials_used;
      } catch { /* no previous selection */ }
    }
    setMaterials(mats);
    setMaterialsUsed((j.materials_consumed || []).map((m) => ({ material_id: m.material_id, product_name: m.product_name, unit: m.unit, quantity: m.quantity ?? "" })));
    setChargeCodes(j.charge_codes || []);
    const _wnd = draftForWorker(j, user.role === "admin" ? (j.current_day_worker_id || j.technician_id) : user.id, j.appointment_date);
    const _draftForDay = _wnd && (_wnd.date || "").slice(0, 10) === (j.appointment_date || "").slice(0, 10) ? _wnd.text : "";
    setDescription(j.description_of_works || _draftForDay || "");
    setNotesSavedAt((_draftForDay && _wnd.updated_at) || null);
    setComments(j.customer_comments || "");
    setCustomerName((j.signature && j.signature.signed_by) || j.contact_person || j.customer_name || "");
    setCustomerMobile(j.phone || "");
    const tos = j.time_on_site || {};
    setArrivalTime(tos.arrival_time || j.start_time || "");
    setFinishTime(tos.finish_time || "");
    setBreakMinutes(tos.break_minutes != null && tos.break_minutes !== 0 ? String(tos.break_minutes) : "");
    setSiteContact({ name: j.contact_person || "", position: j.contact_position || "", phone: j.contact_phone || j.phone || "", email: j.contact_email || "" });
  }).catch((e) => {
    if (e.response?.status === 403) {
      toast.error("You do not have access to this job.");
      navigate(user.role === "admin" ? "/jobs" : user.role === "contractor" ? "/onboarding" : "/my-jobs");
    } else if (e.response?.status === 404) {
      toast.error("Job not found.");
      navigate(user.role === "admin" ? "/jobs" : user.role === "contractor" ? "/onboarding" : "/my-jobs");
    }
  });
  useEffect(() => { load(); }, [id]);

  const multiDefaultApplied = useRef(false);
  const completionRef = useRef(null);
  const [searchParams] = useSearchParams();
  const completeParamApplied = useRef(false);
  useEffect(() => {
    const d = searchParams.get("complete");
    if (job && d && !completeParamApplied.current && user?.role === "admin") {
      completeParamApplied.current = true;
      completeDay(d, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, searchParams]);
  useEffect(() => {
    if (!job || multiDefaultApplied.current) return;
    const worked = new Set((job.dockets || []).filter((d) => !d.archived).map((d) => (d.visit_date || "").slice(0, 10)));
    const today = (job.appointment_date || "").slice(0, 10);
    const hasFuture = (job.schedule_days || []).some((d) => {
      const dt = (d.date || "").slice(0, 10);
      return dt && dt > today && !worked.has(dt);
    });
    if (hasFuture) { setCompletionStatus("multi_day_continue"); multiDefaultApplied.current = true; }
  }, [job]);

  if (!job) return <div className="text-slate-400">Loading job…</div>;
  const isAdmin = user.role === "admin";
  const mapsDest = (job.latitude && job.longitude) ? `${job.latitude},${job.longitude}` : encodeURIComponent(job.site_address || "");
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${mapsDest}`;

  const saveWorkNotes = async () => {
    setSavingNotes(true);
    try {
      const r = await api.post(`/jobs/${job.id}/work-notes-draft`, { text: description, date: job.appointment_date });
      setNotesSavedAt(r.data?.work_notes_draft?.updated_at || new Date().toISOString());
      toast.success("Notes saved — the office can see them now.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingNotes(false); }
  };

  // Linked "Description of Works" box — rendered twice (top + bottom), both bound to the same state.
  const worksNotesBox = (tid, onFocus) => (
    <div data-testid={`${tid}-section`}>
      <Label>Description of Works Completed <span className="text-red-500">*</span></Label>
      <Textarea data-testid={`${tid}-input`} value={description} onFocus={onFocus} onChange={(e) => setDescription(e.target.value)} rows={4} className="mt-1"
        placeholder="Describe exactly what work was completed today. Include the areas completed, any changes requested onsite, additional work carried out and anything important the office or customer should know." />
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <Button type="button" size="sm" variant="outline" data-testid={`${tid}-save-btn`}
          onClick={saveWorkNotes} disabled={savingNotes}>
          {savingNotes ? "Saving…" : "Save Notes"}
        </Button>
        {notesSavedAt && (
          <span data-testid={`${tid}-saved-at`} className="text-xs text-emerald-600">
            Saved {formatDateTime(notesSavedAt)} · visible to the office
          </span>
        )}
        <span className="text-xs text-slate-400 ml-auto">Save your notes anytime through the day — they won't complete the job.</span>
      </div>
    </div>
  );

  const completeDay = async (date, workerId) => {
    try {
      await api.post(`/jobs/${id}/set-current-day`, { date, worker_id: workerId || null });
      await load();
      multiDefaultApplied.current = false;
      setAdminComplete(true);
      setTimeout(() => completionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      toast.success(`Completing ${formatDate(date)} — fill the day's docket and submit.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open that day for completion.");
    }
  };

  const start = async (agreed = false) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/start`, agreed ? { swms_agreed: true } : {});
      toast.success("Job started"); setSwmsDialog(false); setSwmsAgree(false); load();
    } catch (e) {
      const st = e.response?.status;
      const detail = e.response?.data?.detail;
      if (st === 428 && detail?.error === "swms_agreement_required") {
        setSwmsDoc(detail.swms); setSwmsAgree(false); setSwmsDialog(true);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setBusy(false); }
  };

  const materialsValid = materials.length > 0 && materials.every((m) => m.colours && m.colours.length > 0);
  const hoursOnSite = computeHours(arrivalTime, finishTime, breakMinutes);
  const timeValid = arrivalTime && finishTime && hoursOnSite != null && hoursOnSite > 0;
  const chargeCodesMissingQty = chargeCodes.filter((c) => !c.qty || parseFloat(c.qty) <= 0);
  const chargeCodesValid = chargeCodes.length > 0 && chargeCodesMissingQty.length === 0;
  const materialsUsedValid = materialsUsed.some((m) => m.material_id && parseFloat(m.quantity) > 0);
  const returnDateValid = completionStatus !== "awaiting_return_visit" || !!returnVisitDate;
  const baseValid = materialsValid && materialsUsedValid && timeValid && chargeCodesValid && returnDateValid && description.trim() && techSig;
  const guardChargeQty = () => {
    if (!chargeCodesValid) {
      toast.error(chargeCodes.length === 0
        ? "Select at least one charge code and enter its units."
        : `Enter the number of units for: ${chargeCodesMissingQty.map((c) => c.description).join(", ")}`);
      return false;
    }
    return true;
  };
  const canComplete = authMethod === "sign_on_device"
    ? baseValid && customerName.trim() && customerSig
    : baseValid && customerName.trim() && customerEmail.trim();

  const submitCompletion = async () => {
    if (!canComplete) { toast.error("Please complete all required fields."); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/complete`, {
        authorisation_method: authMethod,
        completion_status: completionStatus,
        return_visit_date: completionStatus === "awaiting_return_visit" ? returnVisitDate : null,
        materials_used: materials,
        materials_consumed: materialsUsed.filter((m) => m.material_id && parseFloat(m.quantity) > 0).map((m) => ({ material_id: m.material_id, product_name: m.product_name, unit: m.unit, quantity: parseFloat(m.quantity) || 0 })),
        charge_codes: chargeCodes,
        description_of_works: description, customer_comments: comments,
        contact_person: siteContact.name, contact_position: siteContact.position,
        contact_phone: siteContact.phone, contact_email: siteContact.email,
        arrival_time: arrivalTime, finish_time: finishTime,
        break_minutes: breakMinutes ? parseInt(breakMinutes, 10) : 0,
        hours_on_site: hoursOnSite,
        customer_name: customerName, customer_email: customerEmail, customer_mobile: customerMobile,
        customer_signature: authMethod === "sign_on_device" ? customerSig : null,
        technician_signature: techSig,
      });
      if (authMethod === "send_for_signature") {
        toast.success("Docket sent to customer for signature");
        setTechSig(""); setCustomerSig("");
        await load();
      } else {
        const msg = completionStatus === "ready_for_invoice" ? "Job completed — generating docket…"
          : completionStatus === "return_tomorrow" ? "Docket saved — return scheduled for tomorrow"
          : completionStatus === "multi_day_continue" ? "Today submitted — job continues on the next booked day"
          : sameBillingWeek(job.appointment_date, returnVisitDate) ? "Docket saved — return scheduled, job stays open"
          : "Docket saved — this job is ready to invoice and a new linked job was created for the return visit";
        toast.success(msg);
        setTechSig(""); setCustomerSig("");
        await load();
        if (completionStatus === "ready_for_invoice") docket(false);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    try { await api.post(`/jobs/${job.id}/resend-signing`); toast.success("Signing request resent"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const moveToBilling = async () => {
    if (!window.confirm("Move this completed job to the Ready to Bill queue?")) return;
    setMovingBilling(true);
    try {
      await api.patch(`/jobs/${job.id}/move-to-weekly-billing`, { billing_notes: job.billing_notes || "" });
      toast.success("Moved to Ready to Bill queue");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMovingBilling(false); }
  };

  const docket = async (share) => {
    setDocketBusy(true);
    try {
      const res = await api.get(`/jobs/${job.id}/docket`, { responseType: "blob" });
      const fileName = `${job.job_number || "service-docket"}.pdf`;
      const file = new File([res.data], fileName, { type: "application/pdf" });
      if (share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: fileName, text: `Service Docket ${job.job_number} — True Seal Caulking` });
      } else {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url; a.download = fileName;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      }
    } catch (e) { if (e.name !== "AbortError") toast.error("Could not generate docket"); }
    finally { setDocketBusy(false); }
  };

  const started = job.status !== "scheduled";
  const awaiting = job.status === "awaiting_signature";
  const done = job.status === "completed";
  const returning = job.status === "return_tomorrow" || job.status === "awaiting_return_visit";

  // Multi-day detection: future booked days on this job that haven't been worked yet.
  const _workedSet = new Set((job.dockets || []).filter((d) => !d.archived).map((d) => (d.visit_date || "").slice(0, 10)));
  const _todayDate = (job.appointment_date || "").slice(0, 10);
  const futureDays = (job.schedule_days || [])
    .map((d) => (d.date || "").slice(0, 10))
    .filter((dt) => dt && dt > _todayDate && !_workedSet.has(dt))
    .sort();
  const isMultiDay = futureDays.length > 0;
  const _fmtShort = (dt) => new Date(dt).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  const _fmtLong = (dt) => new Date(dt).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" });
  const futureCountLabel = futureDays.length === 1 ? "1 additional day booked" : `${futureDays.length} additional days booked`;
  const futureDatesText = futureDays.length === 1 ? _fmtLong(futureDays[0]) : futureDays.map(_fmtShort).join(" • ");
  const completionOptions = isMultiDay
    ? [{ key: "multi_day_continue", title: "Multi-Day Job – Additional Days Already Scheduled",
         desc: "Today's work is complete. This job already has future days booked, so no return date needs to be selected." },
       ...COMPLETION_OPTIONS]
    : COMPLETION_OPTIONS;
  const dockets = job.dockets || [];
  const realDockets = dockets.filter((d) => !(d.office_entry && d.notes_outstanding));
  const liveDockets = realDockets.filter((d) => !d.voided);
  const reopened = job.completion_outstanding;
  const jobMaterials = job.materials_used || [];

  const docketByIndex = async (i, dn) => {
    setDocketBusy(true);
    try {
      const res = await api.get(`/jobs/${job.id}/dockets/${i}/docket`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${dn || "docket"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate docket"); }
    finally { setDocketBusy(false); }
  };
  const combinedDocket = async () => {
    setDocketBusy(true);
    try {
      const res = await api.get(`/jobs/${job.id}/combined-docket`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${job.job_number || "combined"}-combined.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate combined docket"); }
    finally { setDocketBusy(false); }
  };
  const timeline = job.timeline || [];

  return (
    <div className="max-w-3xl mx-auto space-y-5 animate-fade-up">
      <button data-testid="back-button" onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-500 text-sm font-medium hover:text-navy">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-navy text-white rounded-2xl p-6 shadow-sm">
        <div className="flex justify-between items-start">
          <div>
            <StatusPill status={job.status} />
            <h1 className="text-2xl sm:text-3xl font-heading font-bold mt-3">{job.customer_name}</h1>
            <p className="text-slate-300 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" />{job.site_address || "No address"}</p>
          </div>
          <span className="text-xs font-mono text-slate-400">{job.job_number}</span>
        </div>
        <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="maps-navigation-button"
          className="mt-4 inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 rounded-lg px-4 py-2 text-sm font-medium transition-colors">
          <Navigation className="w-4 h-4" /> Navigate with Google Maps
        </a>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          {job.builder && <div><p className="text-slate-400 flex items-center gap-1 text-xs uppercase tracking-wider"><Building2 className="w-3.5 h-3.5" />Builder</p><p className="font-medium text-navy mt-0.5">{job.builder}</p></div>}
          {job.contact_person && <div><p className="text-slate-400 flex items-center gap-1 text-xs uppercase tracking-wider"><User className="w-3.5 h-3.5" />Site Contact</p><p className="font-medium text-navy mt-0.5">{job.contact_person}{job.contact_position ? ` · ${job.contact_position}` : ""}</p>{job.contact_phone && <a href={`tel:${job.contact_phone}`} data-testid="contact-phone-link" className="text-blue-700 text-xs font-medium inline-flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{job.contact_phone}</a>}{job.contact_email && <p className="text-xs text-slate-400 mt-0.5">{job.contact_email}</p>}</div>}
          <div><p className="text-slate-400 flex items-center gap-1 text-xs uppercase tracking-wider"><CalendarDays className="w-3.5 h-3.5" />Date</p><p className="font-medium text-navy mt-0.5">{formatDate(job.appointment_date)}</p></div>
          <div><p className="text-slate-400 flex items-center gap-1 text-xs uppercase tracking-wider"><Clock className="w-3.5 h-3.5" />Time</p><p className="font-medium text-navy mt-0.5">{job.start_time || "—"} {job.finish_time && `– ${job.finish_time}`}</p></div>
          {job.technician_name && <div><p className="text-slate-400 text-xs uppercase tracking-wider">Technician</p><p className="font-medium text-navy mt-0.5">{job.technician_name}</p></div>}
        </div>
        {job.phone && <a href={`tel:${job.phone}`} data-testid="call-button" className="flex items-center gap-2 text-blue-700 font-medium text-sm"><Phone className="w-4 h-4" /> {job.phone}</a>}
        {job.scope_of_works && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Scope of Works</p><p className="text-sm text-slate-600 whitespace-pre-line">{job.scope_of_works}</p></div>}
        {job.internal_notes && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Internal Notes</p><p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-line">{job.internal_notes}</p></div>}
      </div>

      {(job.parent_job_id || job.continuation_job_id) && (
        <div data-testid="related-jobs" className="bg-blue-50 border border-blue-200 rounded-2xl p-4 space-y-2">
          <p className="text-xs uppercase tracking-wider text-blue-700 font-semibold flex items-center gap-1.5"><LinkIcon className="w-3.5 h-3.5" />Related Job</p>
          {job.parent_job_id && (
            <button data-testid="related-parent-link" onClick={() => navigate(`/jobs/${job.parent_job_id}`)}
              className="flex items-center gap-2 text-sm text-blue-800 hover:underline">
              <ArrowLeft className="w-3.5 h-3.5" /> Continuation of <span className="font-semibold">{job.parent_job_number}</span>
            </button>
          )}
          {job.continuation_job_id && (
            <button data-testid="related-continuation-link" onClick={() => navigate(`/jobs/${job.continuation_job_id}`)}
              className="flex items-center gap-2 text-sm text-blue-800 hover:underline">
              Continuation job <span className="font-semibold">{job.continuation_job_number}</span> <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {isAdmin && <AdminDayManager job={job} reload={load} onCompleteDay={completeDay} />}

      {isAdmin && !done && !awaiting && !adminComplete && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-4" data-testid="admin-job-manage">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-heading font-bold text-navy text-lg flex items-center gap-2"><History className="w-5 h-5" />Daily Notes &amp; Dockets</h3>
            <div className="flex gap-2">
              {(job.appointment_date || "9999") <= new Date().toISOString().slice(0, 10) && (
                <Button size="sm" variant="outline" data-testid="admin-complete-btn" onClick={() => setAdminComplete(true)}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><CheckCircle2 className="w-4 h-4 mr-1.5" />Complete as admin</Button>
              )}
              <Button size="sm" variant="outline" data-testid="admin-edit-job-btn" onClick={() => setEditOpen(true)}><Pencil className="w-4 h-4 mr-1.5" />Edit job details</Button>
            </div>
          </div>
          {reopened && (
            <div data-testid="admin-reopened-banner" className="text-sm rounded-xl bg-amber-100 border border-amber-300 p-3 text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Re-opened for <b>{reopened.date}</b> — awaiting the worker to fully complete this job on-site. Their pay for that week is on hold until it's done.</span>
            </div>
          )}
          {realDockets.length > 0 && (
            <div className="space-y-2" data-testid="admin-dockets">
              {liveDockets.length >= 2 && (
                <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-indigo-800 flex items-center gap-1.5"><Layers className="w-4 h-4" />Multi-day job · {liveDockets.length} daily dockets</p>
                    <p className="text-xs text-indigo-600 mt-0.5">Roll every day into one Service Docket PDF for the customer.</p>
                  </div>
                  <Button size="sm" data-testid="combined-docket-btn" onClick={combinedDocket} disabled={docketBusy}
                    className="bg-indigo-600 hover:bg-indigo-700 shrink-0"><FileText className="w-4 h-4 mr-1.5" />Combined Docket PDF</Button>
                </div>
              )}
              {realDockets.map((d) => {
                const i = dockets.indexOf(d);
                return (
                <div key={i} data-testid={`admin-docket-row-${i}`} className="border border-slate-100 rounded-lg p-2.5">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-navy">{d.docket_number} <span className="text-slate-400 font-normal">· {d.visit_date}</span>{d.voided && <span data-testid={`admin-docket-voided-${i}`} className="ml-2 text-[10px] font-bold text-red-700 bg-red-100 rounded-full px-2 py-0.5">VOIDED</span>}</p>
                      <p className="text-xs text-slate-500">{TIMELINE_LABELS[`visit_${d.completion_status}`] || d.completion_status}{d.completed_by ? ` · ${d.completed_by}` : ""}{d.notes_source === "office" && d.notes_entered_by ? ` · notes by ${d.notes_entered_by} (office)` : ""}</p>
                      {d.description_of_works && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{d.description_of_works}</p>}
                    </div>
                    <button data-testid={`admin-download-docket-${i}`} onClick={() => docketByIndex(i, d.docket_number)} disabled={docketBusy}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"><FileText className="w-4 h-4" /></button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <AdminNoteEditor jobId={job.id} index={i} initial={d.description_of_works} reload={load} />
                  </div>
                </div>
                );
              })}
            </div>
          )}
          {realDockets.length === 0 && ((job.appointment_date || "9999") <= new Date().toISOString().slice(0, 10)
            ? <p className="text-sm text-slate-500" data-testid="no-notes-hint">No on-site details captured yet. Use the <b>Notes</b> button on a day above to send it back to the worker to complete, or record the office daily notes for that specific day.</p>
            : <p className="text-sm text-slate-500" data-testid="future-job-note">This job is scheduled for the future. You'll be able to record or re-open daily notes on or after the job date.</p>)}
        </div>
      )}

      {!isAdmin && !started && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <Button data-testid="start-job-button" onClick={() => start(false)} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-base">
            <Play className="w-5 h-5 mr-2" /> Start Job
          </Button>
        </div>
      )}

      <Dialog open={swmsDialog} onOpenChange={(o) => { if (!o) { setSwmsDialog(false); setSwmsAgree(false); } }}>
        <DialogContent data-testid="swms-agreement-dialog">
          <DialogHeader><DialogTitle>Safe Work Method Statement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Before starting this job you must confirm you have read and agree to the current Safe Work Method Statement (SWMS) on file{swmsDoc?.version ? ` (v${swmsDoc.version})` : ""}.</p>
            <a href={`${API}/swms/file?auth=${localStorage.getItem("ts_token")}`} target="_blank" rel="noreferrer" data-testid="swms-agreement-view"
               className="inline-flex items-center gap-2 text-blue-700 font-medium text-sm border border-blue-200 bg-blue-50 rounded-lg px-3 py-2">
              <FileText className="w-4 h-4" />View SWMS ({swmsDoc?.filename || "PDF"})
            </a>
            <label className="flex items-start gap-2 cursor-pointer text-sm text-navy">
              <input type="checkbox" checked={swmsAgree} onChange={(e) => setSwmsAgree(e.target.checked)} data-testid="swms-agree-checkbox" className="w-4 h-4 mt-0.5" />
              I have read and agree to the Safe Work Method Statement.
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setSwmsDialog(false); setSwmsAgree(false); }}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!swmsAgree || busy} onClick={() => start(true)} data-testid="swms-agree-confirm">
                <Play className="w-4 h-4 mr-2" />Agree &amp; Start Job
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {((!isAdmin && started) || (isAdmin && (done || awaiting || adminComplete))) && (
        <div ref={completionRef} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-6">
          {isAdmin && adminComplete && !done && !awaiting && (
            <div data-testid="admin-complete-banner" className="flex items-start justify-between gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3">
              <p className="text-sm text-emerald-800 flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Completing this day docket on behalf of the assigned worker. Fill in the on-site details below and finish as normal — no Start or SWMS step is required.</span></p>
              <Button size="sm" variant="ghost" data-testid="admin-complete-cancel" onClick={() => setAdminComplete(false)} className="text-slate-500 shrink-0">Cancel</Button>
            </div>
          )}
          {returning && (
            <div data-testid="return-visit-banner" className={`flex items-start gap-2 rounded-lg py-3 px-4 text-sm font-medium ${job.status === "return_tomorrow" ? "bg-orange-50 text-orange-800" : "bg-purple-50 text-purple-800"}`}>
              <RotateCcw className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                {job.status === "return_tomorrow" ? "Return Tomorrow" : "Awaiting Return Visit"} · {dockets.length} docket{dockets.length === 1 ? "" : "s"} completed so far.
                <p className="text-xs font-normal mt-0.5">Complete a new daily docket for this return visit below.</p>
              </div>
            </div>
          )}

          {dockets.length > 0 && (
            <div data-testid="previous-dockets" className="border border-slate-100 rounded-xl p-4">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2 flex items-center gap-1"><History className="w-3.5 h-3.5" />Completed Dockets ({dockets.length})</h4>
              {isAdmin && liveDockets.length >= 2 && (
                <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-indigo-800 flex items-center gap-1.5"><Layers className="w-4 h-4" />Multi-day job · {liveDockets.length} daily dockets</p>
                    <p className="text-xs text-indigo-600 mt-0.5">Roll every day into one Service Docket PDF for the customer.</p>
                  </div>
                  <Button size="sm" data-testid="combined-docket-btn-completed" onClick={combinedDocket} disabled={docketBusy}
                    className="bg-indigo-600 hover:bg-indigo-700 shrink-0"><FileText className="w-4 h-4 mr-1.5" />Combined Docket PDF</Button>
                </div>
              )}
              <div className="space-y-2">
                {dockets.map((d, i) => (
                  <div key={i} data-testid={`docket-row-${i}`} className="border border-slate-100 rounded-lg p-2.5">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-navy">{d.docket_number} <span className="text-slate-400 font-normal">· {d.visit_date}</span>{d.voided && <span data-testid={`docket-voided-${i}`} className="ml-2 text-[10px] font-bold text-red-700 bg-red-100 rounded-full px-2 py-0.5">VOIDED</span>}</p>
                        <p className="text-xs text-slate-500">{TIMELINE_LABELS[`visit_${d.completion_status}`] || d.completion_status}{d.completed_by ? ` · ${d.completed_by}` : ""}{d.notes_source === "office" && d.notes_entered_by ? ` · notes by ${d.notes_entered_by} (office)` : ""}</p>
                        {d.description_of_works && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{d.description_of_works}</p>}
                      </div>
                      <button data-testid={`download-docket-${i}`} onClick={() => docketByIndex(i, d.docket_number)} disabled={docketBusy}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"><FileText className="w-4 h-4" /></button>
                    </div>
                    {isAdmin && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <AdminNoteEditor jobId={job.id} index={i} initial={d.description_of_works} reload={load} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {isAdmin && !adminComplete && dockets.length === 0 && (job.appointment_date || "9999") <= new Date().toISOString().slice(0, 10) && (
            <p className="text-sm text-slate-500" data-testid="no-notes-hint-2">No on-site details captured yet. Use the <b>Notes</b> button on a day in the list above to send it back to the worker, or record the office daily notes for that day.</p>
          )}

          <PhotoSection job={job} type="before" reload={load} disabled={done || awaiting} />
          <PhotoSection job={job} type="after" reload={load} disabled={done || awaiting} />

          <div className="border-t border-slate-100 pt-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-navy text-lg flex items-center gap-2"><Droplet className="w-5 h-5" /> Service Completion</h3>
              {isAdmin && (done || awaiting || returning) && (
                <Button data-testid="admin-edit-button" variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="w-4 h-4 mr-1.5" />Edit
                </Button>
              )}
            </div>

            {(done || awaiting) ? (
              <div className="mt-4 space-y-4 text-sm">
                <div data-testid="materials-summary">
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Sealant Used</p>
                  {jobMaterials.length === 0 ? <p className="text-navy">—</p> : (
                    <div className="space-y-2">
                      {jobMaterials.map((m, mi) => (
                        <div key={mi} className="bg-slate-50 rounded-lg p-3">
                          <p className="font-semibold text-navy">{m.product}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {(m.colours || []).map((c, ci) => (
                              <span key={ci} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full pl-1.5 pr-2 py-0.5 text-xs text-navy">
                                <span className="inline-block w-3.5 h-3.5 rounded-full border border-slate-300" style={{ background: c.hex || colourHex(m.product, c.name) }} />{c.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {(job.materials_consumed || []).length > 0 && (
                  <div data-testid="materials-used-summary">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Materials Used <span className="normal-case text-slate-400">· internal costing</span></p>
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                      {(job.materials_consumed || []).map((m, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2" data-testid={`material-used-${i}`}>
                          <span className="text-navy">{m.product_name}</span>
                          <span className="text-slate-500">{m.quantity} {m.unit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {isAdmin && chargeLines?.lines?.length > 0 ? (
                  <div data-testid="charge-codes-summary">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Charge Codes</p>
                    {chargeLines.warning_count > 0 && (
                      <div data-testid="charge-warning-banner" className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-xs mb-2">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>{chargeLines.warning_count} line{chargeLines.warning_count > 1 ? "s need" : " needs"} attention before invoicing — check highlighted rows below.</span>
                      </div>
                    )}
                    <div className="overflow-hidden rounded-lg border border-slate-200">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <th className="text-left font-semibold px-3 py-2">Charge Code</th>
                            <th className="text-right font-semibold px-3 py-2">Qty</th>
                            <th className="text-left font-semibold px-2 py-2">Unit</th>
                            <th className="text-right font-semibold px-3 py-2">Rate</th>
                            <th className="text-right font-semibold px-3 py-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chargeLines.lines.map((l, i) => (
                            <tr key={i} className={`border-t border-slate-100 ${l.unpriced || l.under_minimum ? "bg-amber-50/60" : ""}`} data-testid={`charge-line-${i}`}>
                              <td className="px-3 py-2 text-navy font-medium">{l.description}</td>
                              <td className="px-3 py-2 text-right text-slate-600">
                                {l.qty}
                                {l.under_minimum && <span className="ml-1 inline-block text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1 py-0.5 align-middle" data-testid={`charge-under-min-${i}`} title={`Below minimum ${l.min_lm}lm`}>under min {l.min_lm}lm</span>}
                              </td>
                              <td className="px-2 py-2 text-slate-500">{l.unit || "—"}</td>
                              <td className="px-3 py-2 text-right text-slate-600">
                                {l.unpriced
                                  ? <span className="inline-block text-[10px] font-semibold text-red-700 bg-red-100 rounded px-1.5 py-0.5" data-testid={`charge-unpriced-${i}`}>$0 — unpriced</span>
                                  : money(l.rate)}
                                {l.rate_overridden && !l.unpriced && <span className="ml-1 inline-block text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1 py-0.5 align-middle" title="Admin rate override">override</span>}
                              </td>
                              <td className="px-3 py-2 text-right font-semibold text-navy">{money(l.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-slate-200 bg-slate-50/60">
                            <td colSpan={4} className="px-3 py-2 text-right text-slate-500">Subtotal</td>
                            <td className="px-3 py-2 text-right font-semibold text-navy" data-testid="charge-subtotal">{money(chargeLines.subtotal)}</td>
                          </tr>
                          <tr className="bg-slate-50/60">
                            <td colSpan={4} className="px-3 py-1 text-right text-slate-500">GST (10%)</td>
                            <td className="px-3 py-1 text-right text-slate-600">{money(chargeLines.gst)}</td>
                          </tr>
                          <tr className="bg-slate-50/60 border-t border-slate-200">
                            <td colSpan={4} className="px-3 py-2 text-right font-semibold text-navy">Job Total (inc GST)</td>
                            <td className="px-3 py-2 text-right font-bold text-navy" data-testid="charge-total">{money(chargeLines.total_inc_gst)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1.5">Read-only — matches the invoice sent to Xero. Edit via the Edit button above.</p>
                    {chargeLines.financials && (
                      <div data-testid="job-profitability" className="mt-4 border border-slate-200 rounded-xl overflow-hidden">
                        <div className="bg-navy/5 px-4 py-2 flex items-center justify-between">
                          <p className="font-semibold text-navy text-sm">Profitability</p>
                          <p className="text-[11px] text-slate-400 uppercase tracking-wide">Office only · not shown to customer</p>
                        </div>
                        <div className="px-4 py-3 text-sm space-y-0.5">
                          <div className="flex justify-between"><span className="text-slate-500">Revenue</span><span className="text-navy">{money(chargeLines.financials.revenue)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Labour {chargeLines.financials.labour_basis === "metre" ? `(${chargeLines.financials.metres}m @ metre rate)` : `(${chargeLines.financials.hours}h${chargeLines.financials.is_saturday ? " · Sat rate" : ""})`}</span><span className="text-red-500">{chargeLines.financials.labour_cost > 0 ? `-${money(chargeLines.financials.labour_cost)}` : money(0)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Materials</span><span className="text-red-500">{chargeLines.financials.material_cost > 0 ? `-${money(chargeLines.financials.material_cost)}` : money(0)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Overhead</span><span className="text-red-500">{chargeLines.financials.overhead_cost > 0 ? `-${money(chargeLines.financials.overhead_cost)}` : money(0)}</span></div>
                          <div className="flex justify-between font-bold border-t border-slate-200 pt-1 mt-1"><span className="text-navy">Gross Profit</span><span data-testid="job-gross-profit" className={chargeLines.financials.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}>{money(chargeLines.financials.gross_profit)} ({chargeLines.financials.margin}%)</span></div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (job.charge_codes || []).length > 0 && (
                  <div data-testid="charge-codes-summary">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Charge Codes</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(job.charge_codes || []).map((c, i) => (
                        <span key={i} className="bg-navy/5 text-navy border border-navy/10 rounded-full px-2.5 py-1 text-xs font-medium">
                          {c.description}{c.qty ? ` — ${c.qty}${c.unit === "lm" ? "lm" : c.unit === "day" ? " day" : ""}` : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {job.time_on_site && (job.time_on_site.arrival_time || job.time_on_site.finish_time) && (
                  <div data-testid="time-on-site-summary">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Time on Site</p>
                    <p className="text-navy flex items-center gap-1.5">
                      <Timer className="w-4 h-4 text-slate-400" />
                      {job.time_on_site.arrival_time || "—"} → {job.time_on_site.finish_time || "—"}
                      {job.time_on_site.break_minutes ? ` · ${job.time_on_site.break_minutes} min break` : ""}
                      {job.time_on_site.hours_on_site != null ? ` · ${job.time_on_site.hours_on_site} hrs` : ""}
                    </p>
                  </div>
                )}
                {job.description_of_works && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Description of Works Completed</p><p className="text-slate-600 whitespace-pre-line">{job.description_of_works}</p></div>}
                {job.customer_comments && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Customer Comments</p><p className="text-slate-600 whitespace-pre-line bg-slate-50 rounded-lg p-3">{job.customer_comments}</p></div>}
                <div className="grid grid-cols-2 gap-4">
                  {job.signature && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Customer Signature</p><img src={fileUrl(job.signature.path)} alt="customer sig" data-testid="signature-image" className="h-16 object-contain border border-slate-200 rounded-lg p-1" /><p className="text-xs text-navy mt-1">{job.signature.signed_by}</p></div>}
                  {job.technician_signature && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Technician Signature</p><img src={fileUrl(job.technician_signature.path)} alt="tech sig" className="h-16 object-contain border border-slate-200 rounded-lg p-1" /><p className="text-xs text-navy mt-1">{job.technician_signature.signed_by}</p></div>}
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-5">
                <div>
                  <Label>Job Status After This Visit <span className="text-red-500">*</span></Label>
                  {isMultiDay && (
                    <div data-testid="multi-day-banner" className="mt-2 mb-1 rounded-xl border border-indigo-200 bg-indigo-50 p-3">
                      <p className="text-[11px] font-bold tracking-widest text-indigo-700">MULTI-DAY JOB</p>
                      <p className="text-sm font-semibold text-indigo-900 mt-0.5">{futureDays.length === 1 ? "1 day still booked" : `${futureDays.length} days still booked`}</p>
                      <p className="text-xs text-indigo-700 mt-0.5">{futureDatesText}</p>
                      <p className="text-[11px] text-indigo-600/80 mt-1.5">You don't need to choose Return Tomorrow or a return date — just finish today and this job continues on the next booked day.</p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-2 mt-1" data-testid="completion-status-group">
                    {completionOptions.map((o) => (
                      <button key={o.key} type="button" data-testid={`completion-status-${o.key}`}
                        onClick={() => setCompletionStatus(o.key)}
                        className={`text-left rounded-lg border p-3 transition-colors ${completionStatus === o.key ? "border-navy bg-navy/5 ring-1 ring-navy" : "border-slate-200 hover:bg-slate-50"}`}>
                        <p className="text-sm font-semibold text-navy">{o.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{o.desc}</p>
                      </button>
                    ))}
                  </div>
                  {completionStatus === "multi_day_continue" && (
                    <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-start gap-2" data-testid="multi-day-info">
                      <CalendarDays className="w-4 h-4 mt-0.5 shrink-0 text-indigo-700" />
                      <div>
                        <p className="text-sm font-semibold text-indigo-900">{futureCountLabel}</p>
                        <p className="text-xs text-indigo-700 mt-0.5">{futureDatesText}</p>
                      </div>
                    </div>
                  )}
                  {completionStatus === "awaiting_return_visit" && (
                    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="return-visit-picker">
                      <Label>Return Visit Date <span className="text-red-500">*</span></Label>
                      <p className="text-xs text-slate-400 mt-0.5 mb-2">Select the date you need to return. If it falls in a later week (Mon–Sun), the office will be set up with a new linked job automatically.</p>
                      <Input data-testid="return-visit-date-input" type="date" value={returnVisitDate}
                        min={job.appointment_date || undefined}
                        onChange={(e) => setReturnVisitDate(e.target.value)} className="max-w-xs" />
                      {returnVisitDate && (
                        sameBillingWeek(job.appointment_date, returnVisitDate)
                          ? <p className="text-xs text-navy mt-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />Return stays on this job (same billing week). Keep collecting daily dockets.</p>
                          : <p className="text-xs text-amber-700 mt-2 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />This return is in a later week — a new linked continuation job will be created and this job will move to Waiting to Invoice.</p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Site Contact / Foreman</Label>
                  <p className="text-xs text-slate-400 mt-0.5 mb-2">Confirm who was on site today — update if a different foreman or contact was present.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input data-testid="complete-contact-name" value={siteContact.name} onChange={(e) => setSiteContact((c) => ({ ...c, name: e.target.value }))} placeholder="Contact name" />
                    <Input data-testid="complete-contact-title" value={siteContact.position} onChange={(e) => setSiteContact((c) => ({ ...c, position: e.target.value }))} placeholder="Title / Position" />
                    <Input data-testid="complete-contact-phone" value={siteContact.phone} onChange={(e) => setSiteContact((c) => ({ ...c, phone: e.target.value }))} placeholder="Contact phone" />
                    <Input data-testid="complete-contact-email" value={siteContact.email} onChange={(e) => setSiteContact((c) => ({ ...c, email: e.target.value }))} placeholder="Email address" />
                  </div>
                </div>
                <div>
                  <Label>Actual Time on Site <span className="text-red-500">*</span></Label>
                  <p className="text-xs text-slate-400 mt-0.5 mb-2">Record the real hours worked on site today.</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Start Time</Label>
                      <Input data-testid="arrival-time-input" type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Finish Time</Label>
                      <Input data-testid="finish-time-input" type="time" value={finishTime} onChange={(e) => setFinishTime(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Break (mins)</Label>
                      <Input data-testid="break-minutes-input" type="number" min="0" step="5" value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} placeholder="0" className="mt-1" />
                    </div>
                  </div>
                  <div className="mt-2 text-sm" data-testid="hours-on-site-display">
                    {hoursOnSite != null && hoursOnSite > 0
                      ? <span className="inline-flex items-center gap-1.5 text-navy font-semibold"><Timer className="w-4 h-4" />{hoursOnSite} hours on site</span>
                      : (arrivalTime && finishTime) ? <span className="text-red-500 text-xs">Finish time must be after start time.</span>
                      : <span className="text-slate-400 text-xs">Enter start and finish times to calculate hours.</span>}
                  </div>
                </div>
                {worksNotesBox("description-works-top")}
                <div>
                  <Label>Materials Used <span className="text-red-500">*</span></Label>
                  <p className="text-xs text-slate-400 mt-0.5 mb-2">Select each material used on site and the quantity. (No pricing shown — costs are calculated in the office.)</p>
                  <MaterialsUsedPicker value={materialsUsed} onChange={setMaterialsUsed} />
                  {!materialsUsedValid && (
                    <div data-testid="materials-used-alert" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs mt-2">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>Add at least one material with a quantity.</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Colour Choices <span className="text-red-500">*</span></Label>
                  <p className="text-xs text-slate-400 mt-0.5 mb-2">Add each Pasco product used and tick the colour(s) applied.</p>
                  <MaterialsPicker value={materials} onChange={setMaterials} />
                </div>
                <div>
                  <Label>Charge Codes <span className="text-red-500">*</span></Label>
                  <p className="text-xs text-slate-400 mt-0.5 mb-2">Select all that apply to this visit (descriptions only). Enter the number of units for each.</p>
                  <ChargeCodesPicker value={chargeCodes} onChange={setChargeCodes} jobId={id} />
                  {!chargeCodesValid && (
                    <div data-testid="charge-qty-alert" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs mt-2">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{chargeCodes.length === 0
                        ? "Select at least one charge code and enter its units."
                        : <>Enter the number of units for: <span className="font-semibold">{chargeCodesMissingQty.map((c) => c.description).join(", ")}</span></>}</span>
                    </div>
                  )}
                </div>
                <div>
                  {worksNotesBox("description-works", guardChargeQty)}
                </div>
                <div>
                  <Label>Customer Comments</Label>
                  <Textarea data-testid="customer-comments-input" value={comments} onChange={(e) => setComments(e.target.value)} rows={2} className="mt-1" placeholder="Record any customer comments or feedback." />
                </div>

                {/* True Seal Authorisation */}
                <div className="border-t border-slate-100 pt-4">
                  <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">True Seal Authorisation</h4>
                  <p className="text-sm mb-2">Technician: <span className="font-medium text-navy" data-testid="authorisation-technician-name">{isAdmin ? (job.technician_name || user.name) : user.name}</span></p>
                  <CaptureSignature value={techSig} onChange={setTechSig} testid="technician-signature" />
                  <p className="text-xs text-slate-400 mt-2">Recorded: {formatDateTime(new Date())}</p>
                </div>

                {/* Client Authorisation */}
                <div className="border-t border-slate-100 pt-4">
                  <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">Client Authorisation</h4>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button type="button" data-testid="auth-sign-device" onClick={() => setAuthMethod("sign_on_device")}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border transition-colors ${authMethod === "sign_on_device" ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}>
                      <Smartphone className="w-4 h-4" />Sign on Device
                    </button>
                    <button type="button" data-testid="auth-send-signature" onClick={() => setAuthMethod("send_for_signature")}
                      className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border transition-colors ${authMethod === "send_for_signature" ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}>
                      <Send className="w-4 h-4" />Send for Signature
                    </button>
                  </div>

                  <Label>Customer Name <span className="text-red-500">*</span></Label>
                  <Input data-testid="customer-name-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="mt-1 mb-3" placeholder="Customer full name" />

                  {authMethod === "sign_on_device" ? (
                    <>
                      <p className="text-sm text-slate-500 mb-2">I confirm the works listed above have been completed to my satisfaction.</p>
                      <CaptureSignature value={customerSig} onChange={setCustomerSig} testid="customer-signature" />
                      <p className="text-xs text-slate-400 mt-2">Completion Date: {formatDate(new Date())}</p>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-500">Nobody available to sign on-site? Send the docket to the customer to review and e-sign.</p>
                      <div>
                        <Label>Customer Email <span className="text-red-500">*</span></Label>
                        <div className="relative mt-1"><Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <Input data-testid="customer-email-input" type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="pl-9" placeholder="customer@email.com" /></div>
                      </div>
                      <div>
                        <Label>Customer Mobile (optional)</Label>
                        <Input data-testid="customer-mobile-input" value={customerMobile} onChange={(e) => setCustomerMobile(e.target.value)} className="mt-1" placeholder="04xx xxx xxx" />
                      </div>
                    </div>
                  )}
                </div>

                <Button data-testid="complete-job-button" onClick={submitCompletion} disabled={busy || !canComplete}
                  className="w-full bg-navy hover:bg-navy-light py-6 text-base disabled:opacity-50">
                  {authMethod === "send_for_signature"
                    ? <><Send className="w-5 h-5 mr-2" />{busy ? "Sending…" : "Send Docket for Signature"}</>
                    : <><CheckCircle2 className="w-5 h-5 mr-2" />{busy ? "Saving…" : (completionStatus === "multi_day_continue" ? "Submit Today" : completionStatus === "return_tomorrow" ? "Save Docket & Return Tomorrow" : completionStatus === "awaiting_return_visit" ? "Save Docket & Await Return" : "Complete Job & Ready for Invoice")}</>}
                </Button>
                {!canComplete && <p className="text-xs text-center text-slate-400">Complete all required fields (*){authMethod === "sign_on_device" ? " and both signatures" : ", email & technician signature"} to finish.</p>}
              </div>
            )}
          </div>

          {/* Awaiting signature panel */}
          {awaiting && (
            <div className="border-t border-slate-100 pt-5 space-y-4">
              <div className="flex items-center gap-2 bg-yellow-50 text-yellow-800 rounded-lg py-3 px-4 font-medium">
                <Timer className="w-5 h-5" /> Awaiting Customer Signature
              </div>
              <p className="text-sm text-slate-500">Sent to <span className="font-medium text-navy">{job.signing?.customer_email}</span>{job.signing?.sent_at && ` on ${formatDateTime(job.signing.sent_at)}`}. The link expires in 30 days.</p>
              <div className="flex gap-2">
                <Button data-testid="resend-signing-button" onClick={resend} variant="outline" className="flex-1"><Send className="w-4 h-4 mr-2" />Resend Signing Request</Button>
                <Button data-testid="preview-docket-button" onClick={() => docket(false)} disabled={docketBusy} variant="outline"><FileText className="w-4 h-4 mr-2" />Preview PDF</Button>
              </div>
            </div>
          )}

          {/* Completed + docket */}
          {done && (
            <div className="border-t border-slate-100 pt-5 space-y-4">
              <div className="flex items-center gap-2 justify-center text-emerald-700 bg-emerald-50 rounded-lg py-3 font-medium">
                <CheckCircle2 className="w-5 h-5" /> Job Completed{job.ready_for_invoice ? " · Ready for Invoice" : ""}
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Service Completion Docket</h4>
                <div className="flex gap-2">
                  <Button data-testid="share-docket-button" onClick={() => docket(true)} disabled={docketBusy} className="flex-1 bg-navy hover:bg-navy-light"><Share2 className="w-4 h-4 mr-2" />{docketBusy ? "Preparing…" : "Share / Email Docket"}</Button>
                  <Button data-testid="download-docket-button" onClick={() => docket(false)} disabled={docketBusy} variant="outline"><FileText className="w-4 h-4 mr-2" />PDF</Button>
                </div>
              </div>
              {isAdmin && (
                job.invoice_status === "waiting_to_invoice" ? (
                  <div>
                    <Button data-testid="move-to-billing-button" onClick={moveToBilling}
                      disabled={movingBilling || !job.signature}
                      className="w-full bg-navy hover:bg-navy-light disabled:opacity-50">
                      <Receipt className="w-4 h-4 mr-2" />{movingBilling ? "Moving…" : "Move to Ready to Bill"}
                    </Button>
                    {!job.signature && <p className="text-[11px] text-amber-600 mt-1.5 text-center">Customer signature required before moving to Ready to Bill.</p>}
                  </div>
                ) : job.invoice_status === "ready_for_weekly_invoice" ? (
                  <div data-testid="in-weekly-billing-badge" className="w-full text-center text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg py-3 flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />Waiting in Ready to Bill
                  </div>
                ) : job.invoice_status === "included_in_invoice" ? (
                  <div data-testid="invoiced-badge" className="w-full text-center text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg py-3">
                    Invoiced{job.invoice_number ? ` · ${job.invoice_number}` : ""}
                  </div>
                ) : null
              )}
            </div>
          )}

          {/* Timeline */}
          {timeline.length > 0 && (
            <div className="border-t border-slate-100 pt-5">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3 flex items-center gap-1"><History className="w-3.5 h-3.5" />Job Timeline</h4>
              <div className="space-y-2">
                {timeline.map((t, i) => (
                  <div key={i} data-testid={`timeline-${t.event}`} className="flex items-center gap-3 text-sm">
                    <span className="w-2 h-2 rounded-full bg-navy shrink-0" />
                    <span className="text-navy">{TIMELINE_LABELS[t.event] || t.event}{t.meta?.by ? ` · ${t.meta.by}` : ""}</span>
                    <span className="text-slate-400 text-xs ml-auto">{formatDateTime(t.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(job.audit_log || []).length > 0 && (
            <div className="border-t border-slate-100 pt-5" data-testid="audit-history">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3 flex items-center gap-1"><History className="w-3.5 h-3.5" />Audit History</h4>
              <div className="space-y-2">
                {[...job.audit_log].reverse().map((a, i) => (
                  <div key={i} data-testid={`audit-${i}`} className="text-sm border border-slate-100 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-navy">{a.label}</span>
                      <span className="text-slate-400 text-xs">{a.admin} · {formatDateTime(a.at)}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5"><span className="line-through text-red-400">{a.old}</span> → <span className="text-emerald-600">{a.new}</span></p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {isAdmin && <AdminEditJob open={editOpen} setOpen={setEditOpen} job={job} onSaved={load} />}
    </div>
  );
}
